import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Container,
  Stack,
  Paper,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  HelpOutlineRounded as HelpOutlineRoundedIcon,
  ChatBubbleOutlineRounded as ChatBubbleOutlineRoundedIcon,
} from '@mui/icons-material';
import WelcomeScreen from './WelcomeScreen';
import MessageList from './MessageList';
import LeftSidebar from './LeftSidebar';
import {
  Message,
  FavoritePrompt,
  sendMessage,
  sendMessageStream,
  renameConversation,
  deleteConversation,
  listFavoritePrompts,
  createFavoritePrompt,
  deleteFavoritePrompt,
} from '../services/chatService';
import { useAuth } from '../App';

interface Conversation {
  id: string;
  title: string;
  timestamp: Date;
  messages: Message[];
}

const ChatInterface: React.FC = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [titleMenuAnchor, setTitleMenuAnchor] = useState<HTMLElement | null>(null);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editingTitle, setEditingTitle] = useState('');
  const [favoritePrompts, setFavoritePrompts] = useState<FavoritePrompt[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const assistantResponseRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  // Load conversations from backend when user is available
  useEffect(() => {
    if (user) {
      loadUserConversations();
      loadFavoritePrompts();
    }
  }, [user]);

  const loadFavoritePrompts = async () => {
    try {
      setFavoritePrompts(await listFavoritePrompts());
    } catch (error) {
      // The sidebar section just stays empty; nothing else on the page depends
      // on favorites, so this is not worth an error banner on load.
      console.error('Error loading favorite prompts:', error);
    }
  };
  
  const loadUserConversations = async () => {
    if (!user) return;

    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/api/conversations`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        
        // Convert backend conversation format to our format
        const loadedConversations: Conversation[] = data.conversations.map((conv: any) => ({
          id: conv.id,
          title: conv.title || `Conversation ${conv.id.slice(0, 8)}`,
          timestamp: new Date(conv.last_accessed || conv.created_at),
          messages: [] // Messages will be loaded when conversation is selected
        }));
        
        setConversations(loadedConversations);
      }
    } catch (error) {
      console.error('Error loading conversations:', error);
    }
  };
  
  const loadConversationHistory = async (conversationId: string) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE_URL}/chat/history/${conversationId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();

        if (data.history && data.history.length > 0) {
          // Fetch CSV files for this conversation
          let csvFiles: Record<number, any> = {};
          try {
            const csvResponse = await fetch(`${API_BASE_URL}/api/conversations/${conversationId}/csv_files`, {
              credentials: 'include'
            });
            if (csvResponse.ok) {
              const csvData = await csvResponse.json();
              csvFiles = csvData.csv_files || {};
            }
          } catch (csvError) {
            console.error('Error loading CSV files:', csvError);
          }

          // Convert history to Message format and attach CSV metadata by index
          let assistantIndex = 0;
          const loadedMessages: Message[] = data.history.map((msg: any) => {
            const message: Message = {
              id: msg.id || Date.now().toString(),
              type: msg.role === 'user' ? 'user' : 'assistant',
              content: msg.content,
              timestamp: new Date(msg.timestamp || Date.now())
            };

            // Attach CSV metadata if this is an assistant message and has associated CSV
            if (msg.role === 'assistant') {
              const csvData = csvFiles[assistantIndex];
              if (csvData) {
                message.csvMetadata = {
                  full_csv_download: csvData.url,
                  full_csv_row_count: csvData.full_csv_row_count
                };
              }
              assistantIndex++;
            }

            return message;
          });

          setMessages(loadedMessages);

          // Update conversation with loaded messages
          setConversations(prev => prev.map(conv =>
            conv.id === conversationId
              ? { ...conv, messages: loadedMessages }
              : conv
          ));
        }
      }
    } catch (error) {
      console.error('Error loading conversation history:', error);
    }
  };
  
  const scrollToTop = () => {
    
    if (chatContainerRef.current) {
      
      // Try both methods
      chatContainerRef.current.scrollTop = 0; // Immediate scroll
      chatContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' }); // Smooth scroll
      
      // Check if it worked
      setTimeout(() => {
      }, 100);
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Removed automatic scroll on message change - we handle scrolling manually now



  const generateConversationTitle = (firstPrompt: string): string => {
    // Take first 6 words and capitalize first letter
    const words = firstPrompt.trim().split(' ').slice(0, 6);
    let title = words.join(' ');
    
    // Capitalize first letter
    title = title.charAt(0).toUpperCase() + title.slice(1);
    
    if (firstPrompt.length > 50 || words.length < firstPrompt.trim().split(' ').length) {
      title += '...';
    }
    return title;
  };

  // overrideText is how a clicked favorite prompt is sent: the input box is
  // empty then, and setInput would not have applied yet by the time we read it.
  const handleSendMessage = async (overrideText?: string) => {
    const messageContent = (overrideText ?? input).trim();
    if (!messageContent || !user) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: messageContent,
      timestamp: new Date(),
    };

    const isFirstMessage = messages.length === 0;
    let conversationId = currentConversationId;

    // If this is the first message, create a new conversation immediately
    if (isFirstMessage) {
      // Use a temporary ID that will be replaced by OpenAI's conversation ID
      const tempConversationId = `temp_${Date.now()}`;
      const newConversation: Conversation = {
        id: tempConversationId,
        title: generateConversationTitle(messageContent),
        timestamp: new Date(),
        messages: [userMessage], // Start with just the user message
      };

      setConversations(prev => [newConversation, ...prev]);
      setCurrentConversationId(tempConversationId);
      conversationId = tempConversationId;
      
      // After conversation is established via the first message to backend,
      // the backend will automatically save it to Redis
    } else if (conversationId) {
      // Add user message to existing conversation
      setConversations(prev => prev.map(conv => 
        conv.id === conversationId 
          ? { ...conv, messages: [...conv.messages, userMessage], timestamp: new Date() }
          : conv
      ));
    }

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setError(null);
    setIsLoading(true);

    try {
      if (!conversationId) {
        throw new Error('No conversation ID available');
      }

      // Create initial assistant message that will be updated during streaming
      const assistantMessageId = (Date.now() + 1).toString();
      const thinkingStartTime = new Date();
      const assistantMessage: Message = {
        id: assistantMessageId,
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        thinkingStartTime: thinkingStartTime,
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Stream the response - pass title only for first message
      let finalContent = '';
      const titleToSend = isFirstMessage ? generateConversationTitle(messageContent) : undefined;
      const result = await sendMessageStream(messageContent, conversationId, (content: string) => {
        finalContent = content;
        // Clear status when content arrives
        setCurrentStatus(null);
        // Debug logging for streaming updates
        if (content.includes('/api/plots/')) {
        }
        // Update the assistant message content and calculate thinking duration
        setMessages(prev => prev.map(msg => {
          if (msg.id === assistantMessageId) {
            // Calculate thinking duration when first content arrives
            const duration = !msg.content && content && msg.thinkingStartTime
              ? Math.round((new Date().getTime() - new Date(msg.thinkingStartTime).getTime()) / 1000)
              : msg.thinkingDuration;
            return { ...msg, content: content, thinkingDuration: duration };
          }
          return msg;
        }));
      }, titleToSend, (status: string) => {
        setCurrentStatus(status);
      });

      // Add CSV metadata to the assistant message if present
      if (result.csvMetadata) {
        setMessages(prev => prev.map(msg =>
          msg.id === assistantMessageId
            ? { ...msg, csvMetadata: result.csvMetadata }
            : msg
        ));
      }

      // Update conversation ID if OpenAI returned a different one
      if (result.conversationId && result.conversationId !== conversationId) {
        setCurrentConversationId(result.conversationId);

        // Update the conversation in the list with the new ID
        setConversations(prev => prev.map(conv =>
          conv.id === conversationId
            ? { ...conv, id: result.conversationId }
            : conv
        ));
      }

      // Update the conversation with the final assistant's response (use the updated conversation ID)
      const finalConversationId = result.conversationId || conversationId;
      const finalAssistantMessage = { ...assistantMessage, content: finalContent, csvMetadata: result.csvMetadata };
      setConversations(prev => prev.map(conv =>
        conv.id === finalConversationId
          ? { ...conv, messages: [...conv.messages, finalAssistantMessage], timestamp: new Date() }
          : conv
      ));

      // Update conversation title if smart title was generated
      if (result.smartTitle) {
        setConversations(prev => prev.map(conv =>
          conv.id === finalConversationId
            ? { ...conv, title: result.smartTitle! }
            : conv
        ));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        type: 'assistant',
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      setCurrentStatus(null);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    setInput(suggestion);
  };

  const handleNewChat = () => {
    // Prevent creating new chat while loading
    if (isLoading) return;
    
    setMessages([]);
    setError(null);
    setCurrentConversationId(null);
    setInput('');
  };

  const handleRetryMessage = async (userMessageId: string) => {
    // Find the user message to retry
    const messageIndex = messages.findIndex(m => m.id === userMessageId);
    if (messageIndex === -1) return;
    
    const userMessage = messages[messageIndex];
    if (userMessage.type !== 'user') return;
    
    if (!currentConversationId) {
      setError('No active conversation');
      return;
    }
    
    // Remove all messages after this user message (including the failed assistant response)
    const newMessages = messages.slice(0, messageIndex + 1);
    setMessages(newMessages);
    
    // Resend the message
    setError(null);
    setIsLoading(true);
    
    try {
      // Create initial assistant message that will be updated during streaming
      const assistantMessageId = Date.now().toString();
      const thinkingStartTime = new Date();
      const assistantMessage: Message = {
        id: assistantMessageId,
        type: 'assistant',
        content: '',
        timestamp: new Date(),
        thinkingStartTime: thinkingStartTime,
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Stream the response
      let finalContent = '';
      const result = await sendMessageStream(userMessage.content, currentConversationId, (content: string) => {
        finalContent = content;
        setCurrentStatus(null);
        // Update the assistant message content and calculate thinking duration
        setMessages(prev => prev.map(msg => {
          if (msg.id === assistantMessageId) {
            // Calculate thinking duration when first content arrives
            const duration = !msg.content && content && msg.thinkingStartTime
              ? Math.round((new Date().getTime() - new Date(msg.thinkingStartTime).getTime()) / 1000)
              : msg.thinkingDuration;
            return { ...msg, content: content, thinkingDuration: duration };
          }
          return msg;
        }));
      }, undefined, (status: string) => {
        setCurrentStatus(status);
      });

      // Add CSV metadata to the assistant message if present
      if (result.csvMetadata) {
        setMessages(prev => prev.map(msg =>
          msg.id === assistantMessageId
            ? { ...msg, csvMetadata: result.csvMetadata }
            : msg
        ));
      }

      // Update conversation ID if OpenAI returned a different one
      if (result.conversationId && result.conversationId !== currentConversationId) {
        setCurrentConversationId(result.conversationId);

        // Update the conversation in the list with the new ID
        setConversations(prev => prev.map(conv =>
          conv.id === currentConversationId
            ? { ...conv, id: result.conversationId }
            : conv
        ));
      }

      // Update the conversation with the final assistant's response (use the updated conversation ID)
      const finalConversationId = result.conversationId || currentConversationId;
      const finalAssistantMessage = { ...assistantMessage, content: finalContent, csvMetadata: result.csvMetadata };
      setConversations(prev => prev.map(conv =>
        conv.id === finalConversationId
          ? { ...conv, messages: [...conv.messages, finalAssistantMessage], timestamp: new Date() }
          : conv
      ));
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error instanceof Error ? error.message : 'An unexpected error occurred');
    } finally {
      setIsLoading(false);
      setCurrentStatus(null);
    }
  };

  const handleSelectConversation = async (conversationId: string) => {
    // Prevent switching conversations while loading
    if (isLoading) return;

    const conversation = conversations.find(c => c.id === conversationId);
    if (conversation) {
      setCurrentConversationId(conversationId);
      setError(null);

      // ALWAYS clear messages first to prevent showing stale data from previous conversation
      setMessages([]);

      // Always fetch fresh conversation history from backend
      // This prevents race conditions and ensures we always show correct data
      await loadConversationHistory(conversationId);
    }
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleRenameConversation = async (conversationId: string, newTitle: string) => {
    try {
      // Call API to rename on backend
      await renameConversation(conversationId, newTitle);

      // Update local state on success
      setConversations(prev => prev.map(conv =>
        conv.id === conversationId
          ? { ...conv, title: newTitle }
          : conv
      ));
    } catch (error) {
      console.error('Failed to rename conversation:', error);
      setError('Failed to rename conversation. Please try again.');
    }
  };

  const handleDeleteConversation = async (conversationId: string) => {
    try {
      // Call API to delete on backend
      await deleteConversation(conversationId);

      // Update local state on success
      setConversations(prev => prev.filter(conv => conv.id !== conversationId));

      // If we're deleting the current conversation, clear the chat
      if (conversationId === currentConversationId) {
        setMessages([]);
        setCurrentConversationId(null);
        setInput('');
        setError(null);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      setError('Failed to delete conversation. Please try again.');
    }
  };

  // ===== Favorite prompts =====

  const handleSaveFavoritePrompt = async (promptText: string) => {
    try {
      const saved = await createFavoritePrompt(promptText);
      // The server appends, so mirroring that here keeps the sidebar order
      // identical to what the next reload will fetch.
      setFavoritePrompts(prev => [...prev, saved]);
    } catch (err) {
      // Carries the real reason: the 25-prompt cap or the length bound.
      setError(err instanceof Error ? err.message : 'Unable to save this prompt.');
    }
  };

  const handleUseFavoritePrompt = (promptText: string) => {
    if (isLoading) return;
    handleSendMessage(promptText);
  };

  const handleDeleteFavoritePrompt = async (promptId: string) => {
    try {
      await deleteFavoritePrompt(promptId);
      setFavoritePrompts(prev => prev.filter(p => p.id !== promptId));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to delete this favorite prompt.');
    }
  };

  const handleTitleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setTitleMenuAnchor(event.currentTarget);
  };

  const handleTitleMenuClose = () => {
    setTitleMenuAnchor(null);
  };

  const handleStartEditTitle = () => {
    const currentConversation = conversations.find(c => c.id === currentConversationId);
    if (currentConversation) {
      setEditingTitle(currentConversation.title);
      setIsEditingTitle(true);
    }
    handleTitleMenuClose();
  };

  const handleSaveTitle = async () => {
    if (currentConversationId && editingTitle.trim()) {
      try {
        // Call API to rename on backend
        await renameConversation(currentConversationId, editingTitle.trim());

        // Update local state on success
        setConversations(prev => prev.map(conv =>
          conv.id === currentConversationId
            ? { ...conv, title: editingTitle.trim() }
            : conv
        ));
      } catch (error) {
        console.error('Failed to rename conversation:', error);
        setError('Failed to rename conversation. Please try again.');
      }
    }
    setIsEditingTitle(false);
    setEditingTitle('');
  };

  const handleCancelEditTitle = () => {
    setIsEditingTitle(false);
    setEditingTitle('');
  };

  const handleTitleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSaveTitle();
    } else if (event.key === 'Escape') {
      handleCancelEditTitle();
    }
  };

  // If no user, this component shouldn't render (handled by App.tsx authentication)
  if (!user) {
    return (
      <Box 
        sx={{ 
          height: '100vh', 
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        height: '100vh', 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, // Stack on mobile, side-by-side on desktop
        backgroundColor: 'background.default', // Use theme background
        // Print styles
        '@media print': {
          height: 'auto',
          backgroundColor: 'white',
          flexDirection: 'column',
        },
      }}
    >
      {/* Left Sidebar */}
      <Box
        sx={{
          '@media print': {
            display: 'none', // Hide sidebar when printing
          },
        }}
      >
        <LeftSidebar
          conversations={conversations}
          onNewChat={handleNewChat}
          onSelectConversation={handleSelectConversation}
          currentConversationId={currentConversationId}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          onRenameConversation={handleRenameConversation}
          onDeleteConversation={handleDeleteConversation}
          favoritePrompts={favoritePrompts}
          onUseFavoritePrompt={handleUseFavoritePrompt}
          onDeleteFavoritePrompt={handleDeleteFavoritePrompt}
          isLoading={isLoading}
        />
      </Box>

      {/* Main Chat Area */}
      <Box 
        component="main"
        sx={{ 
          flexGrow: 1, 
          height: '100vh', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: 'background.default',
          position: 'relative',
          // Vertical guide line when sidebar is expanded
          '&::before': !sidebarCollapsed ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 0,
            width: '1px',
            height: '100%',
            backgroundColor: 'divider',
            opacity: 0.4,
            pointerEvents: 'none',
            display: { xs: 'none', md: 'block' }, // Only show on desktop
          } : {},
          // Print styles
          '@media print': {
            height: 'auto',
            backgroundColor: 'white',
            '&::before': {
              display: 'none', // Hide guide line when printing
            },
          },
        }}
      >

      {/* Help Icon - Top Right */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pt: 1.5,
          pb: 0,
          '@media print': {
            display: 'none',
          },
        }}
      >
        <Box
          component="a"
          href="https://support.ottimate.com/support/tickets/new"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Share feedback"
          title="Share feedback"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.75,
            height: 36,
            px: 1.75,
            borderRadius: '18px',
            border: '1.5px solid #a6f0db',
            backgroundColor: '#e8f9f5',
            color: '#00796b',
            fontSize: '0.875rem',
            fontWeight: 500,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
            '&:hover': {
              backgroundColor: '#d5f3ec',
              borderColor: '#27c499',
            },
          }}
        >
          <ChatBubbleOutlineRoundedIcon sx={{ fontSize: '1.1rem' }} />
          Got feedback?
        </Box>
        <IconButton
          component="a"
          href="https://support.ottimate.com/support/solutions/articles/14000160384-ottimate-copilot-glossary-faqs"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Help & FAQs"
          title="Help & FAQs"
          sx={{
            color: 'text.secondary',
            border: '1.5px solid',
            borderColor: 'divider',
            width: 36,
            height: 36,
            '&:hover': {
              backgroundColor: 'action.hover',
              borderColor: 'text.secondary',
            },
          }}
        >
          <HelpOutlineRoundedIcon sx={{ fontSize: '1.25rem' }} />
        </IconButton>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ p: 2 }}>
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{ borderRadius: 1 }}
          >
            {error}
          </Alert>
        </Box>
      )}

      {/* Main Chat Area */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'hidden', 
          display: 'flex', 
          flexDirection: 'column',
          backgroundColor: 'background.default', // Force white background
          // Print styles
          '@media print': {
            overflow: 'visible',
            backgroundColor: 'white',
            height: 'auto',
          },
        }}
      >
        {messages.length === 0 ? (
          <WelcomeScreen 
            userName={user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : user?.username}
            onSuggestionClick={handleSuggestionClick}
            input={input}
            setInput={setInput}
            onSendMessage={() => handleSendMessage()}
            onKeyPress={handleKeyPress}
            isLoading={isLoading}
          />
        ) : (
          <>
            {/* Conversation Title */}
            {(currentConversationId || messages.length > 0) && (
              <Box 
                data-testid="conversation-title"
                sx={{ 
                  borderBottom: 1, 
                  borderColor: 'divider',
                  py: 2,
                  backgroundColor: 'background.default',
                }}
              >
                <Box 
                  sx={{ 
                    maxWidth: '960px',
                    margin: '0 auto',
                    px: 3,
                    display: 'flex',
                    justifyContent: 'flex-start', // Left align
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: 1 }}>
                    {isEditingTitle ? (
                      <TextField
                        value={editingTitle}
                        onChange={(e) => setEditingTitle(e.target.value)}
                        onKeyDown={handleTitleKeyPress}
                        onBlur={handleSaveTitle}
                        variant="standard"
                        autoFocus
                        sx={{ 
                          flexGrow: 1,
                          maxWidth: '600px', // Increased width
                          '& .MuiInput-underline:before': {
                            borderBottomColor: 'divider',
                          },
                          '& .MuiInput-underline:hover:before': {
                            borderBottomColor: 'primary.main',
                          },
                        }}
                      />
                    ) : (
                      <>
                        <Box
                          onClick={currentConversationId ? handleTitleMenuOpen : undefined}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: currentConversationId ? 'pointer' : 'default',
                            px: 2,
                            py: 0.75,
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'divider',
                            backgroundColor: 'background.default',
                            minWidth: '300px', // Increased minimum width
                            maxWidth: '600px', // Maximum width
                            '&:hover': currentConversationId ? {
                              backgroundColor: 'action.hover',
                            } : {},
                          }}
                        >
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 500, // Reduced from 600 to 500 for less boldness
                              color: 'text.primary',
                              fontSize: '1.125rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1,
                            }}
                          >
                            {conversations.find(c => c.id === currentConversationId)?.title || 'Current Conversation'}
                          </Typography>
                          {currentConversationId && (
                            <KeyboardArrowDownIcon sx={{ color: 'text.secondary', fontSize: '1.2rem', flexShrink: 0 }} />
                          )}
                        </Box>
                        
                        <Menu
                          anchorEl={titleMenuAnchor}
                          open={Boolean(titleMenuAnchor)}
                          onClose={handleTitleMenuClose}
                          PaperProps={{
                            sx: {
                              mt: 0.5,
                              minWidth: 160,
                            },
                          }}
                        >
                          <MenuItem onClick={handleStartEditTitle}>
                            <ListItemIcon>
                              <EditIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Rename" />
                          </MenuItem>
                        </Menu>
                      </>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            
            <MessageList
              messages={messages}
              isLoading={isLoading}
              assistantResponseRef={assistantResponseRef}
              chatContainerRef={chatContainerRef}
              onRetryMessage={handleRetryMessage}
              currentStatus={currentStatus}
              favoritePrompts={favoritePrompts}
              onSaveFavoritePrompt={handleSaveFavoritePrompt}
            />
            <div ref={messagesEndRef} />
            
            {/* Input Area for conversation mode */}
            <Box 
              sx={{ 
                borderTop: 1, 
                borderColor: 'divider',
                backgroundColor: 'background.default',
                p: 3,
                display: 'flex',
                justifyContent: 'center',
                // Hide input area when printing
                '@media print': {
                  display: 'none',
                },
              }}
            >
              <Box
                sx={{
                  maxWidth: '960px',
                  width: '100%',
                  margin: '0 auto',
                }}
              >
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    backgroundColor: 'background.paper',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: '#a6f0db',
                      boxShadow: '0 2px 8px rgba(39, 196, 153, 0.08)',
                    },
                    '&:focus-within': {
                      borderColor: '#a6f0db',
                      boxShadow: '0 0 0 3px rgba(166, 240, 219, 0.2), 0 4px 12px rgba(39, 196, 153, 0.08)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="flex-end">
                    <TextField
                      fullWidth
                      multiline
                      maxRows={6}
                      variant="standard"
                      placeholder="Ask a follow-up..."
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      disabled={isLoading}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          fontSize: '1rem',
                          lineHeight: 1.5,
                          display: 'flex',
                          alignItems: 'center',
                          minHeight: '40px',
                          '& textarea': {
                            resize: 'none',
                            padding: '8px 0',
                          },
                          '& input': {
                            padding: '8px 0',
                          },
                        },
                      }}
                    />
                    <IconButton
                      onClick={() => handleSendMessage()}
                      disabled={!input.trim() || isLoading}
                      sx={{
                        backgroundColor: input.trim() && !isLoading ? '#e8ecef' : '#f5f7fa',
                        color: input.trim() && !isLoading ? '#4a5568' : '#cbd5e0',
                        borderRadius: 1.5,
                        p: 1.75,
                        minWidth: '48px',
                        minHeight: '48px',
                        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                        transform: 'scale(1)',
                        border: '1.5px solid transparent',
                        '&:hover': {
                          backgroundColor: input.trim() && !isLoading ? '#27c499' : '#f5f7fa',
                          color: input.trim() && !isLoading ? '#ffffff' : '#cbd5e0',
                          transform: input.trim() && !isLoading ? 'scale(1.08)' : 'scale(1)',
                          boxShadow: input.trim() && !isLoading ? '0 4px 16px rgba(39, 196, 153, 0.25)' : 'none',
                          borderColor: input.trim() && !isLoading ? '#27c499' : 'transparent',
                        },
                        '&:active': {
                          transform: input.trim() && !isLoading ? 'scale(0.95)' : 'scale(1)',
                        },
                        '&:disabled': {
                          backgroundColor: '#f5f7fa',
                          color: '#cbd5e0',
                          transform: 'scale(1)',
                        },
                      }}
                    >
                      {isLoading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    </IconButton>
                  </Stack>
                </Paper>
              </Box>
            </Box>
          </>
        )}
      </Box>
      </Box>
    </Box>
  );
};

export default ChatInterface;